import type { SharedContractAccessState } from "@closer/shared";
import { findPermission, RECORD_ACCESS_LEVEL_NAMES } from "@closer/shared";
import { Badge, Button, Card, FormField, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * KURUMUNUZDA ERİŞİMİ OLANLAR (CLOS-1125 · 1157 · rapor §9 · Halit S3/S4/Guard B).
 *
 * Karşı taraf kurumunun KENDİ bağlamında: davet edenin açtığı dış tavan (kapsam) görünür,
 * kurumun yetkili yöneticisi (`contract.share`) hangi çalışanının bu sözleşmeye erişeceğini
 * kendisi dağıtır — üye üye, departmanla değil (MT-S10). Davet eden bu ekrana ULAŞAMAZ; o
 * yalnız taraf kartında erişimli adları görür (S4). Seviye tavanı aşamaz; imza/onay gibi
 * yetkiler yine `tavan ∩ kişinin kendi rolü ∩ workflow` kesişimiyle çalışır.
 */
export function SharedContractAccessSection({ contractId }: { contractId: string }) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const anahtar = tenantKey(tenantId, "paylasilan-sozlesme", contractId, "erisim");
  const [secili, setSecili] = useState("");
  const durum = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<SharedContractAccessState>(`/api/shared-contracts/${contractId}/access`, {
          tenantId,
        })
      ).data,
  });
  const tazele = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: anahtar }),
      queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "paylasilan-sozlesme", contractId),
      }),
    ]);
  };
  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const ver = useMutation({
    mutationFn: async (membershipId: string) =>
      api(`/api/shared-contracts/${contractId}/access`, {
        method: "POST",
        tenantId,
        headers: { "idempotency-key": idempotency.uret() },
        body: JSON.stringify({ membershipId }),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      setSecili("");
      await tazele();
      toast.success("Erişim verildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof ApiRequestError ? err.message : "Erişim verilemedi"),
  });
  const geriCek = useMutation({
    mutationFn: async (membershipId: string) =>
      api(`/api/shared-contracts/${contractId}/access/${membershipId}`, {
        method: "DELETE",
        tenantId,
      }),
    onSuccess: async () => {
      await tazele();
      toast.success("Erişim geri çekildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof ApiRequestError ? err.message : "Erişim geri çekilemedi"),
  });

  if (durum.isPending) {
    return (
      <Card>
        <Skeleton lines={2} />
      </Card>
    );
  }
  /**
   * ⚠ HATADA SESSİZ: bölüm yardımcı; alan bağlamında ya da eski sunucuda uç yoksa (404)
   * paylaşılan sözleşme sayfası hata kartıyla dolmasın. Asıl içerik yukarıda.
   */
  if (durum.error) return null;
  const veri = durum.data;
  /** Kişi katılımında (dağıtım yok) ve tek erişimli kişide bölüm gürültü olurdu. */
  if (!veri.canDistribute && veri.members.length <= 1) return null;
  const adaylar = veri.candidates.filter((a) => !a.hasAccess);
  return (
    <Card>
      <h2 className="mb-1 text-base font-semibold text-fg-heading">Kurumunuzda erişimi olanlar</h2>
      <p className="mb-3 text-xs text-fg-soft" data-testid="erisim-tavani">
        Karşı tarafın bu sözleşme için açtığı kapsam:{" "}
        {veri.ceiling.permissions.map((k) => findPermission(k)?.description ?? k).join(" · ")} ·{" "}
        {RECORD_ACCESS_LEVEL_NAMES[veri.ceiling.accessLevel]}. Kimin erişeceğini kurumunuz belirler;
        kişinin yapabildikleri bu kapsamla kendi rolünün kesişimidir.
      </p>
      <ul className="flex flex-col">
        {veri.members.map((uye) => (
          <li
            key={uye.membershipId}
            data-testid={`erisimli-${uye.membershipId}`}
            className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm text-fg">{uye.name}</span>
              <span className="text-xs text-fg-soft">
                {RECORD_ACCESS_LEVEL_NAMES[uye.level]}
                {uye.acceptor ? " · daveti kabul eden" : ""}
                {uye.grantedByName && !uye.acceptor ? ` · ${uye.grantedByName} verdi` : ""}
              </span>
            </span>
            <span className="flex items-center gap-2">
              <Badge tone="success">Aktif</Badge>
              {veri.canDistribute ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={geriCek.isPending}
                  onClick={() => geriCek.mutate(uye.membershipId)}
                >
                  Erişimi geri çek
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {veri.canDistribute ? (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <FormField label="Erişim verilecek üye">
              <Select
                aria-label="Erişim verilecek üye"
                value={secili}
                onChange={(e) => setSecili(e.target.value)}
              >
                <option value="">Seçiniz</option>
                {adaylar.map((a) => (
                  <option key={a.membershipId} value={a.membershipId}>
                    {a.name} ({a.email})
                  </option>
                ))}
              </Select>
            </FormField>
          </div>
          <Button
            type="button"
            size="sm"
            disabled={secili === ""}
            loading={ver.isPending}
            onClick={() => ver.mutate(secili)}
          >
            Erişim ver
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
